import { useEffect, useState } from 'react'

/**
 * How long a logo URL being typed must stop changing before the preview requests it.
 *
 * Every keystroke of `https://cdn.example.com/logo.svg` is, on the way, a valid-looking URL, and an
 * `<img>` requests whatever it is given. Without a pause the owner's browser would ask a third
 * party's server for every prefix of an address the owner is still typing — and tell it so. A stored
 * value, a saved one and the revert after a failed save skip the wait (`useSettledValue`).
 */
export const LOGO_PREVIEW_DELAY_MS = 400

/**
 * The URL an `<img>` may be given, or `undefined` for anything else.
 *
 * **`https://` and nothing else** — the same rule the API enforces on write (`UpdateBrandingRequest.logoUrl`:
 * a lowercase scheme, no inner whitespace) and the field's help states. Anything else is never handed to an element that would fetch it:
 * not `http://` (the owner's console is served over TLS and the tenant's apps would refuse it),
 * not `javascript:` or `data:`, not a protocol-relative `//host` and not text.
 */
export function previewableLogoUrl(value: string): string | undefined {
  const trimmed = value.trim()
  // The API's own rule, exactly (`UpdateBrandingRequest.logoUrl`): a lowercase `https://` and no
  // whitespace anywhere inside. Leading and trailing spaces are trimmed first, because Save sends
  // the trimmed value; the console never fetches what the API would refuse.
  if (!/^https:\/\/\S+$/.test(trimmed)) {
    return undefined
  }
  try {
    const url = new URL(trimmed)
    return url.protocol === 'https:' && url.hostname !== '' ? trimmed : undefined
  } catch {
    return undefined
  }
}

/**
 * `wanted`, once it has stopped changing for {@link LOGO_PREVIEW_DELAY_MS} — or at once when
 * `immediate`. While immediate it also keeps the settled value current, so the moment the owner
 * starts typing the preview goes on showing what it showed until the new text settles.
 */
export function useSettledValue(wanted: string, immediate: boolean): string {
  const [settled, setSettled] = useState(wanted)

  useEffect(() => {
    if (immediate) {
      setSettled(wanted)
      return undefined
    }
    const timer = setTimeout(() => {
      setSettled(wanted)
    }, LOGO_PREVIEW_DELAY_MS)
    return () => {
      clearTimeout(timer)
    }
  }, [wanted, immediate])

  return immediate ? wanted : settled
}

/**
 * The logo as the URL serves it, in a box that says why when there is nothing to show.
 *
 * Four states, each in words: **blank** (no logo, so the default is used), **not previewable**
 * (not `https://`, so nothing was requested), **loaded**, and **could not be loaded** (the browser
 * reported an error for the URL it was given). The failed state belongs to one URL: a different
 * address starts again.
 *
 * `referrerPolicy="no-referrer"`: this console's address contains a tenant id, and the host of a
 * logo is somebody else's server.
 */
export function LogoPreview({ value }: { readonly value: string }) {
  const trimmed = value.trim()
  const url = previewableLogoUrl(trimmed)

  if (trimmed === '') {
    return <div className="logo-box">No logo set — the default logo is used.</div>
  }
  if (url === undefined) {
    return (
      <div className="logo-box">
        Only an https:// address can be previewed, so nothing was requested.
      </div>
    )
  }
  return <LoadedLogo key={url} url={url} />
}

function LoadedLogo({ url }: { readonly url: string }) {
  const [failed, setFailed] = useState(false)

  return (
    <div className="logo-box">
      {failed ? (
        <span role="status">The image could not be loaded from that address.</span>
      ) : (
        <img
          src={url}
          alt="The logo as the tenant’s apps will show it"
          referrerPolicy="no-referrer"
          decoding="async"
          onError={() => {
            setFailed(true)
          }}
        />
      )}
    </div>
  )
}
